/*  ========================================================================
    # Pagination
    ========================================================================  */

// Dependencies
import React from 'react';
import bem from '@/react/utils/bem';

// Hooks
import useTranslation from '@/react/hooks/useTranslation';

const Pagination = ({
  itemsCount,
  itemsPerPage,
  currentPage,
  handlePagination
}: {
  itemsCount: number;
  itemsPerPage: number;
  currentPage: number;
  handlePagination: (page: number) => void;
}) => {
  const maxPages = Math.ceil(itemsCount / itemsPerPage);
  const __ = useTranslation();

  /**
   * Types.
   */
  const typeNumber = 1;
  const typeSpacer = 2;

  /**
   * Number.
   */
  const paginationNumber = (page: number) => [
    typeNumber,
    <button
      key={page}
      className={bem('c-pagination', 'page-number', { current: page === currentPage })}
      onClick={() => handlePagination(page)}
    >
      {page}
    </button>
  ];

  /**
   * Spacer.
   */
  const paginationSpacer = (page: number) => [
    typeSpacer,
    <div key={page} className='c-pagination__spacer'>
      ...
    </div>
  ];

  /**
   * Add number button if it's the first/last page or if the current page is adjacent; otherwise, add spacer.
   * Also, always include the first and last page buttons.
   */
  const tempPaginationElements = [];
  for (let i = 1; i <= maxPages; i++) {
    if (i === 1 || i === maxPages) {
      tempPaginationElements.push(paginationNumber(i));
    } else if (Math.abs(currentPage - i) <= 1) {
      tempPaginationElements.push(paginationNumber(i));
    } else {
      tempPaginationElements.push(paginationSpacer(i));
    }
  }

  /**
   * Filter function for numbers and spacers.
   */
  const skipDuplicateElements = (item: any, pos: any, arr: any) =>
    item[0] === typeNumber || // Render page number.
    (item[0] === typeSpacer && (pos === 0 || arr[pos - 1][0] !== typeSpacer)); // Skip spacers that are preceded by other spacers.

  /**
   * Filter out duplicate spacers and prepare elements for rendering.
   */
  const paginationElements = tempPaginationElements
    .filter(skipDuplicateElements)
    .map(elem => elem[1]);

  return (
    <div className={bem('c-pagination', '', { hidden: itemsCount <= itemsPerPage })}>
      <button
        className={bem('c-pagination', 'button', { inactive: currentPage - 1 < 1 })}
        onClick={() => {
          if (currentPage - 1 >= 1) handlePagination(currentPage - 1);
        }}
        title={__('pagination_back')}
      >
        {__('pagination_back')}
      </button>
      {paginationElements}
      <button
        className={bem('c-pagination', 'button', { inactive: currentPage + 1 > maxPages })}
        onClick={() => {
          if (currentPage + 1 <= maxPages) handlePagination(currentPage + 1);
        }}
        title={__('pagination_forward')}
      >
        {__('pagination_forward')}
      </button>
    </div>
  );
};

export default Pagination;
